import { Tooltip } from 'antd';
import clsx from 'clsx';
import { NavLink } from 'react-router';
import { useCurrentUser } from '../auth/CurrentUserProvider';
import { OFFICE_SECTION_GROUPS, OFFICE_SECTIONS, type OfficeSection } from './office-sections';

interface SidebarNavProps {
  collapsed: boolean;
  /** Taller rows for fingers, in the phone drawer. */
  touch?: boolean;
}

function SidebarLink({ section, collapsed, touch }: SidebarNavProps & { section: OfficeSection }) {
  const { slug, label, icon: Icon } = section;
  const link = (
    <NavLink
      to={`/${slug}`}
      aria-label={collapsed ? label : undefined}
      className={({ isActive }) =>
        clsx(
          'group flex items-center gap-2.5 rounded-md transition-colors',
          touch ? 'h-10 text-sm' : 'h-8 text-[13px]',
          collapsed ? 'justify-center' : 'px-2.5',
          isActive ? 'bg-sider-selected font-medium text-sider-selected-fg' : 'text-muted hover:bg-sider-selected/60 hover:text-fg',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={clsx('size-4 shrink-0', isActive ? 'text-primary' : 'text-muted group-hover:text-fg')} strokeWidth={isActive ? 2.25 : 1.75} />
          {!collapsed && <span className="truncate">{label}</span>}
        </>
      )}
    </NavLink>
  );

  return collapsed ? (
    <Tooltip title={label} placement="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
}

export function SidebarNav({ collapsed, touch = false }: SidebarNavProps) {
  const { state } = useCurrentUser();
  const permissions = state.status === 'authenticated' ? state.user.permissions : [];
  const allowedSections = OFFICE_SECTIONS.filter((section) => permissions.includes(section.permission));

  return (
    <nav aria-label="Office" className="flex flex-col gap-5 px-2.5">
      {OFFICE_SECTION_GROUPS.map((group) => {
        const sections = allowedSections.filter((s) => s.group === group);
        if (sections.length === 0) return null;

        return (
          <div key={group} className="flex flex-col gap-0.5">
            {collapsed ? (
              <div aria-hidden className="mx-auto mb-1.5 h-px w-5 bg-line" />
            ) : (
              <p className="mb-1 px-2.5 text-[11px] font-medium tracking-wider text-muted uppercase">{group}</p>
            )}
            {sections.map((section) => (
              <SidebarLink key={section.slug} section={section} collapsed={collapsed} touch={touch} />
            ))}
          </div>
        );
      })}
    </nav>
  );
}
